import { Component, EventEmitter, forwardRef, input, Input, OnInit, Output, viewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DictionaryEntryModel, HxDictionaryService, uiLabel } from 'hx-services';
import { NzSelectModeType } from 'ng-zorro-antd/select';

import { NzTreeNode, NzTreeNodeOptions } from 'ng-zorro-antd/tree';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzTreeSelectComponent } from 'ng-zorro-antd/tree-select';
import { NzIconDirective } from 'ng-zorro-antd/icon';

@Component({
  selector: 'hx-dictionary-select',
  templateUrl: './dictionary-select.component.html',
  styleUrls: ['./dictionary-select.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => HxDictionarySelectComponent),
    multi: true
  }],
  imports: [NzTreeSelectComponent, FormsModule, NzIconDirective, TranslocoPipe]
})
export class HxDictionarySelectComponent implements OnInit, ControlValueAccessor {
  treeSelect = viewChild.required(NzTreeSelectComponent);
  dictionaryKey = input.required<string>();
  nzAllowClear = input(false);
  @Input() autoSelect = false;
  @Input() mode: NzSelectModeType = 'default';
  @Input() disabled = false;
  @Input() codeVisible = false;
  @Output() selectChange = new EventEmitter<string | string[] | undefined>();

  options: NzTreeNodeOptions[] = [];
  selected: string | string[] | undefined;

  constructor(
    private dictionaryService: HxDictionaryService,
    private tr: TranslocoService,
  ) {}

  async ngOnInit() {
    const tree = await this.dictionaryService.getEntries({key: this.dictionaryKey(), hierarchy: true});
    const toNodeFn = (ct: DictionaryEntryModel) => {
      const node: NzTreeNodeOptions = {
        key: ct.code,
        title: uiLabel(this.tr.getActiveLang(), ct.title) ?? '',
        enabled: ct.enabled,
        children: ct.childEntries.map(child => toNodeFn(child)),
        isLeaf: ct.childEntries.length === 0,
      };
      return node;
    };

    this.options = tree.map(ct => toNodeFn(ct));
    if (this.autoSelect && (this.options ?? []).length === 1) {
      this.selected = this.options[0].key;
      this.onModelChanged(this.selected);
    }
  }

  onModelChanged(val: any) {
    if (this.mode !== 'multiple') {
      this.onChange(val);
      if (this.selected) {
        this.selectChange.emit(this.selected);
      } else {
        this.selectChange.emit();
      }
    } else {
      const checkedNodes = this.treeSelect().getCheckedNodeList();
      const allCheckedNodes = this.getCheckedNodesFlat(checkedNodes);
      this.selected = allCheckedNodes.map(node => node.key);
      if (Array.isArray(this.selected)) {
        this.selectChange.emit(this.selected);
      } else {
        this.selectChange.emit([]);
      }
    }
  }

  onChange(val: any) {
    // console.log('onChange called', val);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj?: string | string[]): void {
    this.selected = obj;
  }

  private getCheckedNodesFlat(nodes: NzTreeNode[]): NzTreeNode[] {
    let result: NzTreeNode[] = [];

    nodes.forEach(node => {
      if (node.isChecked) {
        result.push(node); // Add checked node to result
      }

      const children = node.getChildren(); // Assuming this returns an array
      if (children && children.length) {
        result = result.concat(this.getCheckedNodesFlat(children)); // Recursively process children
      }
    });

    return result;
  }
}
